/**
 * Every cache that can be holding an appointment, and the one call that marks
 * them stale after a write.
 *
 * There are five of them, filled by five different hooks, and an appointment
 * can sit in four at once — the fifth is a fact about all of them:
 *
 *   `["clients", "appointments", id]`  one client's whole history
 *   `["appointments", "diary", day]`   one day, whole — My task
 *   `["appointments", "day", day]`     one day, cut to what could clash
 *   `["appointments", "detail", id]`   one appointment, read back by id
 *   `["appointments", "ever"]`         whether anything was ever booked
 *
 * Which means a write made through any one of them leaves the other three
 * holding a row that is now wrong. That is not theoretical: marking a session
 * completed on a client's Appointment tab left My task still offering to join
 * it until the page was reloaded, and marking it from My task left the client's
 * own history a version behind. One invalidation written in one place, called
 * by every hook that writes, is what stops that coming back the next time a
 * fifth reader is added.
 *
 * It is deliberately broad. Every day of the diary and the clash cache goes
 * stale, not just the day the row is on, because a reschedule moves an
 * appointment between days and working out which two were touched is a
 * calculation that can be got wrong; "all of them" cannot. The cost is bounded
 * by how TanStack treats it — a query with nothing mounted on it is only
 * marked, and refetches when something next looks at it, so what actually goes
 * out is one request per list currently on screen.
 *
 * Note the two-segment prefixes. `["appointments"]` alone would also match
 * `MEETING_SETTINGS_QUERY_KEY`, which is about the workspace rather than about
 * any appointment and has no reason to be re-read because one was marked.
 */

import type { QueryClient } from "@tanstack/react-query";
import { clientIdOf } from "./appointments";
import type { ClientAppointment } from "./types/appointments.types";

/** One client's whole appointment history — `useClientAppointments`. */
export const clientAppointmentsQueryKey = (clientId: string) =>
  ["clients", "appointments", clientId] as const;

/** Every day of My task's diary — `useDayTasks`. */
export const DAY_DIARY_QUERY_PREFIX = ["appointments", "diary"] as const;

/** Every day the booking dialogs check for clashes — `useDayAppointments`. */
export const DAY_CLASH_QUERY_PREFIX = ["appointments", "day"] as const;

/** One appointment read back by its own id — `useAppointmentDetail`. */
export const appointmentDetailQueryKey = (appointmentId: string) =>
  ["appointments", "detail", appointmentId] as const;

/**
 * Whether this practice has ever booked anything at all — `useEverBooked`.
 *
 * Not a list and not about any one appointment: one boolean, asked once, that
 * decides whether an empty day on My task is a first run or an ordinary free
 * Thursday. It is kept with the rest of them because the write below has to
 * settle it — see there.
 */
export const EVER_BOOKED_QUERY_KEY = ["appointments", "ever"] as const;

/**
 * Marks every cached read that could be holding this appointment stale.
 *
 * Called after a write has already been put into the calling hook's own cache
 * — see `put` in either hook. The two are not redundant and do different jobs:
 * the write-through is what makes the screen somebody is looking at change
 * immediately, and this is what makes every other screen agree with it. The
 * caller's own cache is marked too, which costs it one background refetch and
 * buys the guarantee that no reader is special-cased; nothing flickers for it,
 * because TanStack keeps serving what is cached while it re-reads.
 *
 * @param row the appointment as the server now holds it — the response to the
 * write, not the row the caller had before it.
 */
export function invalidateAppointmentReads(
  queryClient: QueryClient,
  row: ClientAppointment
): void {
  void queryClient.invalidateQueries({ queryKey: DAY_DIARY_QUERY_PREFIX });
  void queryClient.invalidateQueries({ queryKey: DAY_CLASH_QUERY_PREFIX });
  void queryClient.invalidateQueries({
    queryKey: appointmentDetailQueryKey(row.id),
  });

  // Settled rather than marked stale, and it is the one read here that is.
  // There is a row in hand, so the question this answers — has anything ever
  // been booked — has just been answered by the write itself, whichever write
  // it was: cancelling an appointment is still proof one was made. Writing
  // `true` straight in spares a request whose answer is already known, and
  // spares My task a flash of its first-run panel on the next empty day.
  queryClient.setQueryData(EVER_BOOKED_QUERY_KEY, true);

  // Guarded rather than assumed, and twice over. `clientId` is read off
  // `client.public_id`, which is optional on the row — a write that came back
  // without it must still invalidate the three above. And an appointment
  // booked against an enquiry has no client at all: its id would key a
  // history belonging to nobody, so `clientIdOf` answers `null` for it rather
  // than handing over an enquiry's `public_id` to a client's cache.
  const clientId = clientIdOf(row);
  if (clientId) {
    void queryClient.invalidateQueries({
      queryKey: clientAppointmentsQueryKey(clientId),
    });
  }
}
